import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';

import { RfebmPrimaryButtonComponent } from 'src/app/ui/atoms/rfebm-primary-button/rfebm-primary-button.component';
import { RfebmSecondaryButtonComponent } from 'src/app/ui/atoms/rfebm-secondary-button/rfebm-secondary-button.component';
import { AdminDataService, CountryItem } from '../../controller/admin-data.service';

interface UserCreateFormData {
  email: string;
  name: string;
  firstSurname: string;
  secondSurname?: string;
  nif?: string;
  telephone?: string;
  countryIsoCode: string;
  useAlternativeIdentifier: boolean;
}

@Component({
  selector: 'app-create-user-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RfebmPrimaryButtonComponent,
    RfebmSecondaryButtonComponent
  ],
  templateUrl: './create-user-form.component.html',
  styleUrls: ['./create-user-form.component.scss']
})
export class CreateUserFormComponent implements OnInit, OnDestroy {
  private readonly subscriptions: Subscription[] = [];

  userForm: FormGroup;
  countries: CountryItem[] = [];

  constructor(
    private fb: FormBuilder,
    private adminDataService: AdminDataService,
    public dialogRef: MatDialogRef<CreateUserFormComponent>
  ) {}

  ngOnInit(): void {
    this.userForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      name: ['', [Validators.required, Validators.minLength(2)]],
      firstSurname: ['', [Validators.required, Validators.minLength(2)]],
      secondSurname: [''],
      countryIsoCode: ['ES', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]],
      useAlternativeIdentifier: [false],
      nif: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(32)]],
      telephone: ['', [Validators.pattern(/^\d{6,14}$/)]],
    });

    this.subscriptions.push(
      this.userForm.get('useAlternativeIdentifier')!.valueChanges.subscribe(() => {
        this.syncNifValidators();
      })
    );

    this.syncNifValidators();
    this.loadCountries();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get selectedCountryPrefix(): string {
    const isoCode = this.userForm.get('countryIsoCode')?.value;
    const country = this.countries.find(item => item.iso_code === isoCode);
    return country?.phone_prefix || '+34';
  }

  onSave(): void {
    if (!this.userForm.valid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const useAlternativeIdentifier = !!this.userForm.get('useAlternativeIdentifier')?.value;
    const nifValue = (this.userForm.get('nif')?.value || '').trim().toUpperCase();
    const telephoneValue = (this.userForm.get('telephone')?.value || '').trim();

    const payload: UserCreateFormData = {
      email: (this.userForm.get('email')?.value || '').trim(),
      name: (this.userForm.get('name')?.value || '').trim(),
      firstSurname: (this.userForm.get('firstSurname')?.value || '').trim(),
      secondSurname: (this.userForm.get('secondSurname')?.value || '').trim() || undefined,
      countryIsoCode: (this.userForm.get('countryIsoCode')?.value || 'ES').toUpperCase(),
      useAlternativeIdentifier,
      nif: useAlternativeIdentifier ? undefined : (nifValue || undefined),
      telephone: telephoneValue || undefined,
    };

    this.dialogRef.close(payload);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private loadCountries(): void {
    this.adminDataService.getCountries().subscribe({
      next: countries => {
        this.countries = countries;

        const currentIso = this.userForm.get('countryIsoCode')?.value || 'ES';
        const exists = this.countries.some(country => country.iso_code === currentIso);
        if (!exists) {
          this.userForm.patchValue({ countryIsoCode: 'ES' });
        }
      },
      error: () => {
        this.countries = [
          { id: 70, iso_code: 'ES', name: 'Spain', phone_prefix: '+34' },
          { id: 177, iso_code: 'PT', name: 'Portugal', phone_prefix: '+351' },
        ];
      }
    });
  }

  private syncNifValidators(): void {
    const nifControl = this.userForm.get('nif');
    const useAlternativeIdentifier = !!this.userForm.get('useAlternativeIdentifier')?.value;

    if (!nifControl) {
      return;
    }

    if (useAlternativeIdentifier) {
      nifControl.clearValidators();
    } else {
      nifControl.setValidators([Validators.required, Validators.minLength(3), Validators.maxLength(32)]);
    }

    nifControl.updateValueAndValidity({ emitEvent: false });
  }
}
